import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatSelectChange } from '@angular/material/select';
import { Store, select } from '@ngrx/store';
import { DropdownItems } from '@material-library';
import { Subscription } from 'rxjs';
import { SupportTicketService } from 'src/app/account/services/support-ticket.service';
import { ApiService } from 'src/app/core/api.service';
import { Base64Service } from 'src/app/core/base64.service';
import { BlendEvent, ErrCode, User } from 'src/app/shared/models';
import { BlendService } from 'src/app/shared/services/blend.service';
import { environment } from 'src/environments/environment';
import * as fromRoot from '../../../reducers';
import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { DELETE_REASONS } from './delete-reasons.model';
import { SkuService } from 'src/app/shared/services/sku.service';

@Component({
  selector: 'app-dialog-cancel',
  templateUrl: './dialog-cancel.component.html',
  styleUrls: ['./dialog-cancel.component.scss']
})
export class DialogCancelComponent implements OnInit {

  @Input() user: User;
  @Input() showDeleteDialog: boolean;
  @Output() showDeleteDialogChange = new EventEmitter<boolean>();
  @Input() userId: number;
  @Input() plan: number;
  @Input() sig: string;
  @Input() isAccthold: boolean;

  public reasonList: DropdownItems[] = [];
  public specificReasonList: DropdownItems[] = [];
  public cancelForm: FormGroup;
  public isSaveButtonLoading: boolean = false;
  public paidUser: boolean;
  private sub: Subscription;
  public formErrorMessage: string = '';
  public reasonError: string = '';
  public specificReasonError: string = '';
  public descriptionError: string = '';
  public termsError: string = '';
  public errcode: ErrCode;

  constructor(
    private api: ApiService,
    private base64: Base64Service,
    private fb: FormBuilder,
    private ticket: SupportTicketService,
    private blendService: BlendService,
    private snackbarService: SnackbarService,
    private store: Store<fromRoot.State>,
    private skuService: SkuService
  ) { }

  ngOnInit() {
    this.reasonList = [
      { value: '', name: '-- Please choose one --' },
      ...(Object.keys(DELETE_REASONS).map(reason => {
        return {
          name: reason,
          value: reason
        }
      }))
    ];
    this.specificReasonList = [];

    if (this.isAccthold) {
      this.createAcctHoldForm();
      return;
    }

    if (this.user) {
      this.paidUser = this.user.is_pro === 1;
      if (this.sub) {
        this.sub.unsubscribe();
      }
    } else {
      this.sub = this.store
        .pipe(select(fromRoot.getAuthUser))
        .subscribe((data) => {
          this.user = data;
          this.paidUser = this.user.is_pro === 1;
          if (this.sub) {
            this.sub.unsubscribe();
          }
        });
    }
    this.initForm();
  }

  private initForm() {
    this.cancelForm = this.fb.group({
      name: [
        this.base64.decode(this.user.display_name),
        Validators.required,
      ],
      email: [this.user.email, Validators.required],
      mainReason: ['', Validators.required],
      specificReason: ['',Validators.required],
      description: ['',[
        Validators.required,
        this.validateNoBlankSpaces() 
      ]],
      confirm: [false, Validators.pattern('true')],
    });

  }

  private createAcctHoldForm() {
    this.cancelForm = this.fb.group({
        mainReason: ['', Validators.required],
        specificReason: ['',Validators.required],
        description: ['',[
          Validators.required,
          this.validateNoBlankSpaces() 
        ]],
        confirm: ['', Validators.required],
    });
    
  }

 
  private  validateNoBlankSpaces(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const isValid = control.value && control.value.trim().length > 0;
      return isValid ? null : { 'whitespace': true };
    };
  }
  onSelectionChange = (selectionChange: MatSelectChange) => {
    this.cancelForm.controls.mainReason.setValue(selectionChange.value);
    if (this.cancelForm.get('mainReason').valid) {
      this.reasonError = '';
    }

    this.specificReasonList = [
      { value: '', name: '-- Please choose one --' },
      ...(DELETE_REASONS[selectionChange.value].map(reason => {
        return {
          name: reason,
          value: reason
        }
      }))
    ];
  }

  onSpecificReasonChange = (selectionChange: MatSelectChange) => {
    this.cancelForm.controls.specificReason.setValue(selectionChange.value);
    if (this.cancelForm.get('specificReason').valid) {
      this.specificReasonError = '';
    }
    if (this.cancelForm.get('description').valid) {
      this.descriptionError = '';
    }
  }
  
  getDescription = (description: string) => {
    this.cancelForm.controls.description.setValue(description);
    if (this.cancelForm.get('description').valid) {
      this.descriptionError = '';
    }
  }

  getTermsValue = (checkboxChange: MatCheckboxChange) => {
    this.cancelForm.controls.confirm.setValue(checkboxChange.checked);
    if (this.cancelForm.get('description').value === false) this.cancelForm.controls.confirm.setValue('');
    if (this.cancelForm.get('confirm').valid) {
      this.termsError = '';
    }
  }

  isFormValid(): boolean {
    if (!this.cancelForm.valid) {
      this.formErrorMessage = 'Correct the errors in the form below, then try again.';
      this.reasonError = this.cancelForm.get('mainReason').invalid ? 'Select a valid reason.' : '';
      this.specificReasonError = this.cancelForm.get('mainReason').invalid ? 'Select a valid specific reason.' : '';
      this.descriptionError = this.cancelForm.get('description').invalid ? 'Please provide your feedback to continue.' : '';
      this.termsError = this.cancelForm.get('confirm').invalid ? 'Please indicate that you have read and acknowledged the statement above.' : '';
      return false;
    } else {
      this.formErrorMessage = '';
      return true;
    }
  }

  deleteSyncAccount = async () => {
    if (!this.isFormValid()) {
      return;
    }
    this.isSaveButtonLoading = true;
    const reason = this.cancelForm.get('mainReason').value;
    const specificReason = this.cancelForm.get('specificReason').value || 'N/A';
    const seats = await this.skuService.getPanLimit();
    const plan = await this.skuService.getSku();
    await new Promise((resolve) => {
      this.blendService.track(BlendEvent.DELETE_ACCOUNT, {
        reason,
        specific_reason: specificReason,
        selections: '2024-update',
        plan,
        seats
      });
      setTimeout(() => {
        resolve(true);
      }, 3000); //wait for 3 sec
    });
    if (this.paidUser) {
      const isTialUser = this.user.is_on_trial;
      //Auto-delete Account is currently only available for trial use, but it may be used for paid accounts in the future.
      if (isTialUser) {
        try {
          await this.api.execute('cancelzuorasubscription', {});
        } catch (ex) {
          this.isSaveButtonLoading = false;
          if (ex instanceof ErrCode) {
            this.errcode = ErrCode.fromException(ex);
          } else {
            this.closeDeleteDialog();
            this.snackbarService.showMessage("critical", "Couldn’t complete action. Try again.");
          }
        }
      }

      //Call the Zproxy API and disable the auto-renewal when user fills out the cancellation form.
      await this.api.execute('userinfoset', { is_disableautorenew: 1 });

      if (environment.production) {
        const isTrialUserText = isTialUser ? 'Is Trial User: Yes' : '';
        const description = [
          'USER ID: ',
          this.user.uid,
          isTrialUserText,
          `Confirm cancellation: ${this.cancelForm.get('confirm').value ? 'true' : 'false'
          }`,
          '--------',
          `Specific reason: ${specificReason}`,
          '--------',
          'Cancellation request:',
          this.cancelForm.get('description').value,
        ].join('\n\n');

        const postData = {
          z_requester: this.cancelForm.get('email').value,
          z_name: this.cancelForm.get('name').value,
          z_subject: isTialUser
            ? 'Trial User - Cancellation - ' + reason
            : 'Cancellation - ' + reason,
          z_description: description,
          cpversion: '4.0'
        };
        this.ticket.sendSupportTicket(postData);
      } else {
        this.redirectToThankYouPage();
      }
    } else if (this.isAccthold) {
      try {
          await this.api.send('acctcancel', {
              source: 'accthold',
              user: this.userId,
              p: this.plan,
              sig: this.sig,
              subject: this.base64.encode('Cancellation - ' + reason),
              message: this.base64.encode(
                  'Specific reason - ' + specificReason + '. message - ' +
                  this.cancelForm.get('description').value
              ),
          });
          window.location.href = 'https://www.sync.com/support/?thank=you';
      } catch (ex) {
        this.isSaveButtonLoading = false;
        this.errcode = ErrCode.fromException(ex);
      }
    } else {
      // this is a free acct
      try {
        await this.api.execute('acctcancel', {
          source: 'free',
          subject: this.base64.encode('Cancellation - ' + reason),
          message: this.base64.encode(
            'Specific reason - ' + specificReason + '. message - ' +
            this.cancelForm.get('description').value
          ),
        });
        this.redirectToThankYouPage();
      } catch (ex) {
        this.isSaveButtonLoading = false;
        if (ex instanceof ErrCode) {
          this.errcode = ErrCode.fromException(ex);
        } else {
          this.closeDeleteDialog();
          this.snackbarService.showMessage("critical", "Couldn’t complete action. Try again.");
        }
      }
    }
  }
  
  redirectToThankYouPage = () => {
    window.location.href = 'https://www.sync.com/support/?thank=you';
  }

  closeDeleteDialog = () => {
    this.showDeleteDialog = false;
    setTimeout(() => { 
      this.showDeleteDialogChange.next(false);
    });
  }
}
